import { useEffect, useState } from 'react'
import { supabase } from './supabase'

/**
 * Hook que lee el rol del usuario logueado desde la tabla user_profiles.
 *
 * Devuelve:
 *   { role, isAdmin, extraSections, canViewAll, loading }
 *
 * canViewAll     — true si el usuario tiene la bandera can_view_all_projects.
 *                  Permite ver TODOS los proyectos del equipo en modo lectura,
 *                  sin ser admin (no puede editar ni borrar los ajenos).
 */
export function useUserRole(session) {
  const [role, setRole] = useState(null)
  const [extraSections, setExtraSections] = useState([])
  const [canViewAll, setCanViewAll] = useState(false)
  const [pliegoRol, setPliegoRol] = useState(null)
  const [pliegoProjectId, setPliegoProjectId] = useState(null)
  const [pliegoVisibleRamas, setPliegoVisibleRamas] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!session?.user?.id) {
        setRole(null)
        setExtraSections([])
        setCanViewAll(false)
        setPliegoRol(null)
        setPliegoProjectId(null)
        setPliegoVisibleRamas(null)
        setLoading(false)
        return
      }
      setLoading(true)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role, extra_sections, can_view_all_projects, pliego_rol, pliego_project_id, pliego_visible_ramas')
        .eq('user_id', session.user.id)
        .maybeSingle()

      if (cancelled) return

      if (error) {
        console.warn('[useUserRole] Error leyendo perfil, asumiendo user:', error.message)
        setRole('user')
        setExtraSections([])
        setCanViewAll(false)
        setPliegoRol(null)
        setPliegoProjectId(null)
        setPliegoVisibleRamas(null)
      } else {
        setRole(data?.role || 'user')
        setExtraSections(Array.isArray(data?.extra_sections) ? data.extra_sections : [])
        setCanViewAll(!!data?.can_view_all_projects)
        setPliegoRol(data?.pliego_rol || null)
        setPliegoProjectId(data?.pliego_project_id || null)
        setPliegoVisibleRamas(Array.isArray(data?.pliego_visible_ramas) ? data.pliego_visible_ramas : null)
      }
      setLoading(false)
    }

    load()
    return () => { cancelled = true }
  }, [session?.user?.id])

  return {
    role,
    isAdmin: role === 'admin',
    extraSections,
    canViewAll,
    // Modo "invitado al pliego" · usuario externo (arquitectura / ingeniería / contratista)
    // que solo debe ver la sección Requisitos de Pliego filtrada por su rama.
    pliegoRol,
    pliegoProjectId,
    pliegoVisibleRamas,
    isPliegoUser: !!pliegoRol,
    loading,
  }
}
